import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/authService';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import Loader from '../../components/Loader';

const ProfileModal = ({ onClose, user }) => {
   const { refreshUser } = useAuth();
   const [formData, setFormData] = useState({
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
   });
   const [loading, setLoading] = useState(false);

   useEffect(() => {
      if (user) {
         setFormData({
            name: user.name || '',
            email: user.email || '',
            password: '',
            confirmPassword: '',
         });
      }
   }, [user]);

   const handleChange = (e) => {
      setFormData({
         ...formData,
         [e.target.name]: e.target.value,
      });
   };

   const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);

      // Validate password if provided
      if (formData.password) {
         if (formData.password.length < 6) {
            toast.error('Password must be at least 6 characters long');
            setLoading(false);
            return;
         }
         if (formData.password !== formData.confirmPassword) {
            toast.error('Passwords do not match');
            setLoading(false);
            return;
         }
      }

      try {
         const updateData = {
            name: formData.name,
            email: formData.email,
         };

         // Only include password if it's provided
         if (formData.password) {
            updateData.password = formData.password;
         }

         const response = await authService.updateProfile(updateData);

         if (response.success) {
            toast.success('Profile updated successfully!');
            await refreshUser();
            setTimeout(() => {
               onClose();
            }, 1500);
         }
      } catch (err) {
         toast.error(err.response?.data?.error || 'Failed to update profile. Please try again.');
      } finally {
         setLoading(false);
      }
   };

   return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
         <div className="w-full max-w-md rounded-lg bg-white dark:bg-dark-secondary p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
               <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  Update Profile
               </h2>
               <button
                  onClick={onClose}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                  aria-label="Close"
               >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                     />
                  </svg>
               </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
               <Input
                  label="Name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
               />

               <Input
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  required
               />

               <Input
                  label="New Password (leave blank to keep current)"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter new password"
               />

               {formData.password && (
                  <Input
                     label="Confirm New Password"
                     name="confirmPassword"
                     type="password"
                     value={formData.confirmPassword}
                     onChange={handleChange}
                     placeholder="Confirm new password"
                  />
               )}

               <div className="flex gap-3 pt-4">
                  <Button type="button" variant="secondary" onClick={onClose} className="flex-1">
                     Cancel
                  </Button>
                  <Button type="submit" variant="primary" disabled={loading} className="flex-1">
                     {loading ? <Loader size="sm" /> : 'Update Profile'}
                  </Button>
               </div>
            </form>
         </div>
      </div>
   );
};

export default ProfileModal;
